/* eslint-disable no-console */
import * as Sentry from '@sentry/browser';
import type { SeverityLevel } from '@sentry/browser';
import { bootstrappedData as bootstrapped } from './bootstrappedData';

declare const R7Insight: any;

export class Log {
	private static staticAdditionalData: {
		[key: string]: string | number;
	} = {};
	private static isR7Initialized = false;

	public static info(msg: string, additionalData?: unknown): void {
		Log.emitLogMessage('info', msg, additionalData);
	}

	public static warn(msg: string, additionalData?: unknown): void {
		Log.emitLogMessage('warning', msg, additionalData);
	}

	public static error(msg: string, additionalData?: unknown): void {
		Log.emitLogMessage('error', msg, additionalData);
	}

	public static addStaticAdditionalData(
		key: string,
		value: string | number,
	): void {
		Log.staticAdditionalData[key] = value;
	}

	private static emitLogMessage(
		severity: SeverityLevel,
		msg: string,
		additionalData: unknown,
	): void {
		Log.logToConsole(severity, msg, additionalData);
		Log.logToR7Insight(severity, msg, additionalData);
		Log.logToSentry(severity, msg, additionalData);
	}

	private static logToR7Insight(
		severity: SeverityLevel,
		msg: string,
		additionalData: unknown,
	): void {
		if (
			typeof R7Insight === 'undefined' ||
			typeof bootstrapped === 'undefined'
		) {
			return;
		}
		if (!Log.isR7Initialized) {
			R7Insight.init({
				token: bootstrapped.R7InsightToken,
				['page_info']: 'per-page',
				region: 'eu',
			});
			Log.isR7Initialized = true;
		}

		let r7fn: string = severity;
		switch (severity) {
			case 'error':
			case 'info':
			case 'log':
				// These match
				break;
			case 'warning':
				// This exists but with a different name
				r7fn = 'warn';
				break;
			// R7 does not have anything like these.
			case 'debug':
				r7fn = 'info';
				break;
			case 'fatal':
				r7fn = 'error';
				break;
		}

		if (additionalData) {
			R7Insight[r7fn]({
				...Log.staticAdditionalData,
				user: bootstrapped.UserId,
				message: msg,
				data: additionalData,
			});
		} else {
			R7Insight[r7fn]({
				...Log.staticAdditionalData,
				user: bootstrapped.UserId,
				message: msg,
			});
		}
	}

	private static logToSentry(
		severity: SeverityLevel,
		msg: string,
		additionalData: unknown,
	): void {
		if (severity === 'info') {
			return;
		}
		if (typeof Sentry === 'undefined') {
			return;
		}

		if (additionalData instanceof Error) {
			Sentry.captureException(additionalData, { level: severity });
		} else {
			Sentry.captureMessage(msg, { level: severity });
		}
	}

	private static logToConsole(
		severity: SeverityLevel,
		msg: string,
		additionalData: unknown,
	): void {
		if (!console) return;
		switch (severity) {
			case 'fatal':
			case 'error': {
				console.error(msg, additionalData);
				break;
			}
			case 'warning': {
				console.warn(msg, additionalData);
				break;
			}
			default: {
				console.log(msg, additionalData);
				break;
			}
		}
	}
}
